type MobileRelease = Readonly<{
  android?: Readonly<{
    status?: string;
    version?: string;
    url?: string;
    bytes?: number;
    sha256?: string;
    abis?: readonly string[];
  }> | null;
  limitations?: readonly string[];
}>;

let closeActiveDialog: (() => void) | null = null;

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

export function openMobileInstallDialog() {
  if (closeActiveDialog) {
    const existing = document.querySelector<HTMLElement>('.site-admin-mobile-install-dialog');
    existing?.focus();
    return;
  }

  const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const controller = new AbortController();
  const backdrop = element('div', 'site-admin-mobile-install-backdrop');
  const dialog = element('section', 'site-admin-mobile-install-dialog');
  dialog.tabIndex = -1;
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-labelledby', 'site-admin-mobile-install-title');

  const header = element('header');
  const title = element('h2');
  title.id = 'site-admin-mobile-install-title';
  title.textContent = '모바일 앱 설치';
  const closeButton = element('button', 'site-admin-mobile-install-close');
  closeButton.type = 'button';
  closeButton.textContent = '닫기';
  closeButton.setAttribute('aria-label', '모바일 앱 설치 닫기');
  closeButton.title = '닫기';
  header.append(title, closeButton);

  const note = element('p', 'site-admin-mobile-install-note');
  note.textContent =
    '점검·조치와 TBM을 사용하는 Android 앱입니다. 설치 후 기존 운영 계정으로 로그인하세요.';
  const body = element('div', 'site-admin-mobile-install-body');
  const qr = element('img', 'site-admin-mobile-install-qr');
  qr.src = '/mobile-download/qr-mobile-download.svg';
  qr.alt = 'KOP Safety 모바일 앱 다운로드 페이지 QR 코드';
  const details = element('div', 'site-admin-mobile-install-details');
  const status = element('p', 'site-admin-mobile-install-status');
  status.setAttribute('role', 'status');
  status.textContent = '설치 정보를 확인하고 있습니다.';
  const install = element('a', 'site-admin-mobile-install-action');
  install.hidden = true;
  install.textContent = 'Android 앱 설치';
  install.rel = 'noopener';
  const meta = element('p', 'site-admin-mobile-install-meta');
  meta.hidden = true;
  const limits = element('ul', 'site-admin-mobile-install-limits');
  limits.hidden = true;
  details.append(status, install, meta, limits);
  body.append(qr, details);
  dialog.append(header, note, body);
  backdrop.append(dialog);
  document.body.append(backdrop);

  const close = () => {
    if (!closeActiveDialog) return;
    closeActiveDialog = null;
    controller.abort();
    observer.disconnect();
    document.removeEventListener('keydown', onKeyDown, true);
    backdrop.remove();
    previous?.focus();
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...dialog.querySelectorAll<HTMLElement>('button, [href], [tabindex]')].filter(
      (node) => !node.hasAttribute('disabled') && !node.hidden,
    );
    if (!focusable.length) return;
    const first = focusable[0]!;
    const last = focusable.at(-1)!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  const observer = new MutationObserver(() => {
    if (!document.querySelector('.site-admin-dialog')) close();
  });
  closeActiveDialog = close;
  closeButton.addEventListener('click', close);
  backdrop.addEventListener('mousedown', (event) => {
    if (event.target === backdrop) close();
  });
  document.addEventListener('keydown', onKeyDown, true);
  observer.observe(document.body, { childList: true, subtree: true });
  closeButton.focus();

  void fetch('/mobile-download/release.json', { cache: 'no-store', signal: controller.signal })
    .then((response) => (response.ok ? response.json() : Promise.reject(new Error('release'))))
    .then((release: MobileRelease) => {
      const android = release.android;
      const url = android?.url ? new URL(android.url, window.location.origin) : null;
      const valid =
        ['installation-test', 'field-test'].includes(android?.status ?? '') &&
        url?.origin === window.location.origin &&
        url.protocol === 'https:' &&
        /^[a-f0-9]{64}$/i.test(android?.sha256 ?? '');
      if (!valid || !url || !android) throw new Error('release');
      status.textContent = `Android ${android.version ?? '앱'} 버전을 받을 수 있습니다.`;
      install.href = url.href;
      install.hidden = false;
      const size = Number(android.bytes);
      meta.textContent = `${android.abis?.join(', ') || 'Android'} · ${Number.isFinite(size) ? `${(size / 1024 / 1024).toFixed(1)} MB` : '크기 확인 필요'}`;
      meta.hidden = false;
      for (const item of release.limitations ?? []) {
        const row = element('li');
        row.textContent = String(item);
        limits.append(row);
      }
      limits.hidden = limits.childElementCount === 0;
    })
    .catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      status.textContent = '설치 정보를 불러오지 못했습니다. 잠시 후 다시 확인하세요.';
    });
}
